import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getSelectedYear } from 'app/slices/GamesSlice';
import { getSeasonStandings, setStandingsForSeason } from 'app/slices/RankingSlice';
import useSeasonDatasetRefresh from 'hooks/useSeasonDatasetRefresh';
import { useCallback } from 'react';
import { FirebaseCollections, ITeamStandings, Stores } from 'types';

export type UseDataLoaderForStandings = ReturnType<typeof useDataLoaderForStandings>;

/**
 * Loads the selected season's standings and refreshes when its standings completion
 * timestamp advances. The shared coordinator waits for initialization, reuses current
 * cached data, and deduplicates requests across mounted consumers.
 *
 * Each worker request reads the whole season. Responses may include other cached years;
 * the callback selects the requested season and replaces only that season in Redux.
 * A late response cannot erase another season or change the current page selection.
 * Worker failures preserve existing standings; ordinary rerenders do not retry failures.
 * No backend action is triggered and no new completion signal is introduced.
 *
 * @return {UseDataLoaderForStandings} Empty object; consumers read Redux selectors.
 */
const useDataLoaderForStandings = () => {
  const dispatch = useAppDispatch();
  const seasonID = useAppSelector(getSelectedYear);
  const standings = useAppSelector(getSeasonStandings);
  const onLoaded = useCallback((records: ITeamStandings[]) => {
    dispatch(setStandingsForSeason({
      seasonID,
      standings: records.filter((record) => record.seasonWeekID.startsWith(`${seasonID}-`)),
    }));
  }, [dispatch, seasonID]);
  useSeasonDatasetRefresh({
    store: Stores.SeasonStandings,
    collection: FirebaseCollections.Standings,
    seasonID,
    hasData: standings.some((record) => record.seasonWeekID.startsWith(`${seasonID}-`)),
    onLoaded,
  });
  return {};
};

export default useDataLoaderForStandings;
